import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { CheckCircle2, MapPin, Phone } from "lucide-react";

const GRADES = ["Kindergarten", "Grade 1", "Grade 2", "Grade 3", "Grade 4",
                  "Grade 5", "Grade 6", "Grade 7", "Grade 8", "Grade 9",
                  "Grade 10", "Grade 11", "Grade 12", "Other"];

export default function FreeAssessment() {
  useSeo({
    title: "Book a Free Academic Assessment — Teacher4Me Learning Centre, NW Calgary",
    description:
      "Free, no-obligation academic assessment for your child. Math & Science tutoring in NW Calgary. Personalised learning plans. Alberta curriculum support.",
    canonical: "https://teacher4me.ca/free-assessment",
  });

  const [parentName, setParentName] = useState("");
  const [studentName, setStudentName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [grade, setGrade] = useState("");
  const [school, setSchool] = useState("");
  const [subject, setSubject] = useState("math");
  const [concern, setConcern] = useState("");
  const [contact, setContact] = useState("phone");
  const [source, setSource] = useState("website");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/api/centre/leads", {
        parent_name: parentName, student_name: studentName,
        phone, email, grade, school_name: school,
        subject, concern, preferred_contact: contact, source,
      });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="lead-thankyou">
        <div className="bg-[#ECFDF5] border border-[#A7F3D0] rounded-2xl p-10">
          <CheckCircle2 className="w-14 h-14 text-[#064E3B] mx-auto" />
          <h1 className="font-display font-black text-3xl mt-4">Thank you.</h1>
          <p className="text-[#065F46] mt-3">
            Teacher4Me will contact you to arrange your child's free assessment. Usually
            within one business day.
          </p>
          <div className="mt-6 text-sm text-[#4B5563] space-y-1">
            <div className="flex items-center justify-center gap-2">
              <Phone className="w-4 h-4" /> <a href="tel:8253658805" className="font-semibold">825-365-8805</a>
              <span>·</span>
              <a href="https://wa.me/18253658805" target="_blank" rel="noreferrer" className="font-semibold text-[#064E3B]">WhatsApp</a>
            </div>
            <div className="flex items-center justify-center gap-2">
              <MapPin className="w-4 h-4" /> Unit 201A, 4716 16 Ave NW, Calgary
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-10">
      <div className="mb-6">
        <div className="label-eyebrow flex items-center gap-2">
          <MapPin className="w-3 h-3 text-[#D92D20]" /> Teacher4Me Learning Centre · NW Calgary
        </div>
        <h1 className="font-display font-black text-3xl sm:text-4xl mt-2">
          Book a <span className="text-[#D92D20]">FREE</span> academic assessment
        </h1>
        <p className="text-sm text-[#4B5563] mt-3 max-w-2xl">
          Personalised academic support based on your child's grade, ability and learning needs.
          Fill in this quick form — we'll contact you to arrange the assessment.
        </p>
      </div>

      <form onSubmit={submit} className="bg-white border border-[#E5E7EB] rounded-2xl p-6 space-y-4"
            data-testid="lead-form">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Parent name*" value={parentName} setValue={setParentName} testid="lead-parent" required />
          <Field label="Student name*" value={studentName} setValue={setStudentName} testid="lead-student" required />
          <Field label="Phone*" value={phone} setValue={setPhone} testid="lead-phone" required
                 type="tel" placeholder="825-000-0000" />
          <Field label="Email*" value={email} setValue={setEmail} testid="lead-email" required type="email" />
          <Select label="Student grade*" value={grade} setValue={setGrade} testid="lead-grade"
                  required options={["", ...GRADES]} placeholder="Select grade" />
          <Field label="School name (optional)" value={school} setValue={setSchool} testid="lead-school" />
        </div>

        <div>
          <label className="text-sm font-semibold">Subject needed*</label>
          <div className="flex flex-wrap gap-2 mt-2" data-testid="lead-subject-group">
            {[
              { v: "math", l: "Math" },
              { v: "science", l: "Science" },
              { v: "both", l: "Both" },
              { v: "other", l: "Other" },
            ].map((o) => (
              <button type="button" key={o.v} onClick={() => setSubject(o.v)}
                data-testid={`lead-subject-${o.v}`}
                className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                  subject === o.v
                    ? "bg-[#D92D20] text-white border-[#D92D20]"
                    : "bg-white text-[#111827] border-[#E5E7EB] hover:border-[#D92D20]"
                }`}>{o.l}</button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-sm font-semibold">Main concern (short)</label>
          <textarea data-testid="lead-concern" rows={3} value={concern}
            onChange={(e) => setConcern(e.target.value)}
            placeholder="e.g. Struggling with fractions and word problems"
            className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#D92D20]" />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-semibold">Preferred contact</label>
            <div className="flex gap-2 mt-2" data-testid="lead-contact-group">
              {["phone", "text", "email"].map((c) => (
                <button type="button" key={c} onClick={() => setContact(c)}
                  data-testid={`lead-contact-${c}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border capitalize ${
                    contact === c
                      ? "bg-[#064E3B] text-white border-[#064E3B]"
                      : "bg-white text-[#111827] border-[#E5E7EB]"
                  }`}>{c}</button>
              ))}
            </div>
          </div>
          <Select label="How did you hear about us?" value={source} setValue={setSource}
                  testid="lead-source" options={["website", "google", "facebook", "instagram",
                                                   "whatsapp", "referral", "walk_in", "other"]} />
        </div>

        <div className="pt-2 border-t border-[#E5E7EB]">
          <button type="submit" disabled={busy} data-testid="lead-submit"
            className="btn-primary w-full sm:w-auto">
            {busy ? "Submitting..." : "REQUEST FREE ASSESSMENT"}
          </button>
          <p className="text-xs text-[#4B5563] mt-3">
            No obligation. We'll contact you within one business day.
          </p>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, setValue, testid, type = "text", required, placeholder }) {
  return (
    <div>
      <label className="text-sm font-semibold">{label}</label>
      <input data-testid={testid} type={type} required={required} placeholder={placeholder}
        value={value} onChange={(e) => setValue(e.target.value)}
        className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#D92D20]" />
    </div>
  );
}

function Select({ label, value, setValue, testid, options, required, placeholder }) {
  return (
    <div>
      <label className="text-sm font-semibold">{label}</label>
      <select data-testid={testid} required={required} value={value}
        onChange={(e) => setValue(e.target.value)}
        className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#D92D20]">
        {options.map((o) => (
          <option key={o} value={o}>{o === "" ? (placeholder || "Select…") : o}</option>
        ))}
      </select>
    </div>
  );
}
